import { MetricEvent } from "../../requests/metricEvents";

export type HeatmapSmoothingPoint = {
  x: number;
  y: number;
  value: number;
};

export type HeatmapSmoothingOptions = {
  gridSizeMeters?: number;
  sigma?: number;
  metricMode: "meters" | "occurrence";
};

export const DEFAULT_GRID_SIZE_METERS = 2;
const DEFAULT_SIGMA = 3;
const PITCH_LENGTH_METERS = 120;
const PITCH_WIDTH_METERS = 70;
const MIN_COVERAGE_THRESHOLD = 1e-2;

const createGrid = (rows: number, cols: number, initialValue = 0) =>
  Array.from({ length: rows }, () => Array(cols).fill(initialValue));

const convolveWithKernel = (
  source: number[][],
  kernel: number[][],
  kernelSum: number,
  radius: number
): number[][] => {
  const rows = source.length;
  const cols = source[0]?.length ?? 0;
  const output = createGrid(rows, cols);

  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < cols; x += 1) {
      let acc = 0;
      for (let ky = -radius; ky <= radius; ky += 1) {
        for (let kx = -radius; kx <= radius; kx += 1) {
          const yy = y + ky;
          const xx = x + kx;
          if (yy >= 0 && yy < rows && xx >= 0 && xx < cols) {
            acc += source[yy][xx] * kernel[ky + radius][kx + radius];
          }
        }
      }
      output[y][x] = acc / kernelSum;
    }
  }

  return output;
};

export const buildSmoothedHeatmapPoints = (
  events: MetricEvent[],
  {
    gridSizeMeters = DEFAULT_GRID_SIZE_METERS,
    sigma = DEFAULT_SIGMA,
    metricMode,
  }: HeatmapSmoothingOptions
): HeatmapSmoothingPoint[] => {
  if (!events.length) {
    return [];
  }

  const xCells = Math.ceil(PITCH_LENGTH_METERS / gridSizeMeters);
  const yCells = Math.ceil(PITCH_WIDTH_METERS / gridSizeMeters);

  const valueGrid = createGrid(yCells, xCells);
  const coverageGrid = createGrid(yCells, xCells);

  events.forEach((event) => {
    if (event.possession !== 1) {
      return;
    }

    const xIdx = Math.max(0, Math.min(xCells - 1, Math.floor((event.x + 10) / gridSizeMeters)));
    const yIdx = Math.max(0, Math.min(yCells - 1, Math.floor(event.y / gridSizeMeters)));

    const value = metricMode === "meters" ? Math.max(event.meters_gained, 0) : 1;
    valueGrid[yIdx][xIdx] += value;
    coverageGrid[yIdx][xIdx] += 1;
  });

  const radius = Math.ceil(sigma * 3);
  const kernelSize = radius * 2 + 1;
  const kernel: number[][] = createGrid(kernelSize, kernelSize);

  let kernelSum = 0;
  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      const weight = Math.exp(-(dx * dx + dy * dy) / (2 * sigma * sigma));
      kernel[dy + radius][dx + radius] = weight;
      kernelSum += weight;
    }
  }

  if (kernelSum === 0) {
    return [];
  }

  const smoothedValues = convolveWithKernel(valueGrid, kernel, kernelSum, radius);
  const smoothedCoverage = convolveWithKernel(coverageGrid, kernel, kernelSum, radius);

  const halfCell = gridSizeMeters / 2;

  return smoothedValues.flatMap((row, rowIdx) =>
    row.flatMap((value, colIdx) => {
      const coverage = smoothedCoverage[rowIdx][colIdx];
      if (coverage <= MIN_COVERAGE_THRESHOLD) {
        return [];
      }

      return {
        x: colIdx * gridSizeMeters + halfCell - 10,
        y: rowIdx * gridSizeMeters + halfCell,
        value,
      } satisfies HeatmapSmoothingPoint;
    })
  );
};
